/* eslint-disable react-hooks/rules-of-hooks */
import { UeElement } from '@oneaudi/falcon-tools';
import { TextLink, TextButton, ButtonGroup } from '@oneaudi/unified-web-components';
import { TextButton as TextButtonAlpha } from '@oneaudi/unified-web-alpha-components';
import React, { useCallback } from 'react';
import { LayerContentHTML } from '@oneaudi/fa-one-layer';
import styled from 'styled-components';
import type { Margin } from '@oneaudi/unified-web-common';
import type { Alignment, ButtonItem, NumbersItem, TextItem } from '../../types';
import { ModuleType } from '../../types';
import { setHeadlessUrl } from '../utils/setHeadlessUrl';
import { useTracking } from '../hooks/useTracking';
import type { LayerManager } from '../feature-service-types';
import { FocusLayerSize } from '../feature-service-types';

interface EditorialCTAProps {
  mode: ModuleType;
  cta: ButtonItem;
  alignment: Alignment;
  layerManager?: LayerManager;
  ctaComponentList?: NumbersItem[] | TextItem[];
}

interface StyledButtonGroupProps {
  alignment: Alignment;
}

const StyledButtonGroup = styled(ButtonGroup)<StyledButtonGroupProps>`
  justify-content: ${(props) => props.alignment}!important;
`;

export const EditorialCTA: React.FC<EditorialCTAProps> = ({
  mode,
  cta,
  layerManager,
  alignment,
  ctaComponentList,
}) => {
  const hasValidCTA = cta.label?.length > 0 && cta.url?.length > 0;
  if (!hasValidCTA) return null;
  const tracking = useTracking({
    implementer: 2,
  });
  const handleOpenInLayer = useCallback((url: string) => {
    layerManager?.openFocusLayer(
      () => <LayerContentHTML enableDeepLinking url={setHeadlessUrl(url)} />,
      {},
      {
        userCloseable: true,
        size: FocusLayerSize.A,
      },
    );
  }, []);

  const trackClickEvent = () => {
    tracking.sendClickEvent(
      cta.label,
      cta.url,

      cta.variant === 'text'
        ? cta.openInTabOrLayer === 'layer' || cta.url.includes('#oneLayer=/')
          ? 'text link - layer open'
          : 'text link'
        : cta.openInTabOrLayer === 'layer' || cta.url.includes('#oneLayer=/')
          ? 'cta - layer open'
          : 'cta',

      cta.variant === 'text' ? 'text link' : 'button',
      cta.openInTabOrLayer === 'layer' ? 'layer' : 'other',
      ctaComponentList
        ? ctaComponentList.findIndex(
            (component) => JSON.stringify(component.cta) === JSON.stringify(cta),
          ) + 1
        : undefined,
    );
  };

  const onCtaClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (cta?.openInTabOrLayer === 'layer') {
      e.preventDefault();
      handleOpenInLayer(cta.url);
    }
    trackClickEvent();
  };

  const commonButtonProps = {
    'aria-label': cta.ariaLabel || cta.label,
    target: cta.openInTabOrLayer === 'tab' ? '_blank' : '_self',
    variant: 'secondary' as const,
    isFullWidth: true,
    href: cta.url,
    onClick: onCtaClick,
    'data-testid': 'text-component-button',
    margin: ['var(--spacing-relative-lg)', 0, 0, 0] as Margin,
  };

  const renderCta = () => {
    if (cta.variant === 'text' && mode !== ModuleType.NUMBERS) {
      return (
        <TextLink
          aria-label={cta.ariaLabel || cta.label}
          variant="standalone"
          icon="forward"
          iconPosition="trailing"
          href={cta.url}
          target={cta.openInTabOrLayer === 'tab' ? '_blank' : '_self'}
          onClick={onCtaClick}
          size="sm"
          data-testid="text-component-button"
          margin={['var(--spacing-relative-lg)', 0, 0, 0]}
        >
          <UeElement type="text" property="cta_label" label="Label">
            {cta.label}
          </UeElement>
        </TextLink>
      );
    }
    if (process.env.IS_LEGACY) {
      return (
        <TextButton {...commonButtonProps} variant="secondary" size="md">
          <UeElement type="text" property="cta_label" label="Label">
            {cta.label}
          </UeElement>
        </TextButton>
      );
    }
    return (
      <TextButtonAlpha {...commonButtonProps} variant="secondary" size="xs">
        <UeElement type="text" property="cta_label" label="Label">
          {cta.label}
        </UeElement>
      </TextButtonAlpha>
    );
  };

  return (
    <StyledButtonGroup mode="hug" alignment={alignment}>
      {renderCta()}
      <span style={{ display: 'none' }} />
    </StyledButtonGroup>
  );
};
